import {
  Button,
  ClockTimerIcon,
  IconButton,
  PauseIcon,
  PlayIcon,
  XCloseIcon,
} from "@minervaproject/fds";
import clsx from "clsx";
import { throttle } from "lodash";
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Draggable from "react-draggable";

import { MobileCard } from "../../shared/mobile_components";

import { endPollSessionHelper } from "api/api_helpers";
import { FlightAPIContext } from "contexts/flight_api_context";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useTimeRemaining } from "hooks/use_time_remaining";
import { Settings } from "settings";
import {
  adjustBreakoutSessionDuration,
  adjustTimerDuration,
  endBreakoutSession,
  endTimer,
  pauseTimer,
  playTimer,
} from "shared/reducers/classroom";
import { KeyboardShortcutSignalType, PollSessionStatusType, SidebarPanelType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";
import { setKeyboardShortcutSignal } from "store/reducers/local";
import { playTickingAudio, playTimerEndAudio, stopTickingAudio } from "utils/audio";

import "./timer_controls.scss";

const TICKING_SOUND_STARTS_AT_MS = 11 * 1000;
// We play the timer finished sound slightly before the timer ends so that the noise better coincides with the finish.
const TIMER_FINISHED_NOTIFICATION_STARTS_AT_MS = 1 * 1000;
const TEXT_ORANGE_AT_MS = 30 * 1000;
const TEXT_RED_AT_MS = 10 * 1000;

function ClassTimer({
  hasControls,
  canPlaySounds,
  onChangeTime,
  onClose,
  onPlayPause,
}: {
  hasControls: boolean;
  canPlaySounds: boolean;
  onChangeTime: (s: number) => void;
  onClose: () => void;
  onPlayPause: () => void;
}) {
  const classTimer = useAppSelector(classroomSelectors.selectTimer());
  const timerEndDate = classTimer.endsOn ? new Date(classTimer.endsOn) : null;
  const { timeRemainingMs: activeTimeRemainingMs, timeRemainingString: activeTimeRemainingString } =
    useTimeRemaining(timerEndDate);
  const timeRemainingMs =
    (classTimer.active ? activeTimeRemainingMs : classTimer.timeRemainingWhenPausedMs) || 0;
  const pausedTimeRemainingString = (() => {
    const totalSeconds = Math.floor(Math.max(timeRemainingMs, 0) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  })();
  const timeRemainingString = classTimer.active
    ? activeTimeRemainingString
    : pausedTimeRemainingString;
  const [isFlashing, setIsFlashing] = useState(false);
  const [isTickingSoundPlaying, setIsTickingSoundPlaying] = useState(false);

  const wrapperClasses = clsx(
    "timer-view bg-white shadow-level-5 cursor-grab body",
    !hasControls && "no-controls",
    isFlashing && "timer-flashing",
    timeRemainingMs < TEXT_RED_AT_MS
      ? "text-red"
      : timeRemainingMs < TEXT_ORANGE_AT_MS
        ? "text-orange"
        : "text-green",
  );

  useEffect(() => {
    if (timeRemainingMs && classTimer.active) {
      if (
        canPlaySounds &&
        timeRemainingMs <= TICKING_SOUND_STARTS_AT_MS &&
        !isTickingSoundPlaying
      ) {
        setIsTickingSoundPlaying(true);
        playTickingAudio();
      } else if (timeRemainingMs > TICKING_SOUND_STARTS_AT_MS && isTickingSoundPlaying) {
        setIsTickingSoundPlaying(false);
        stopTickingAudio();
      }
      if (timeRemainingMs <= TIMER_FINISHED_NOTIFICATION_STARTS_AT_MS && !isFlashing) {
        setIsFlashing(true);
        if (canPlaySounds) {
          playTimerEndAudio();
        }
        setTimeout(onClose, 3000);
      } else if (timeRemainingMs > TIMER_FINISHED_NOTIFICATION_STARTS_AT_MS && isFlashing) {
        setIsFlashing(false);
      }
    }
  }, [
    canPlaySounds,
    timeRemainingMs,
    isFlashing,
    isTickingSoundPlaying,
    classTimer.active,
    onClose,
  ]);

  useEffect(() => {
    return () => {
      stopTickingAudio();
    };
  }, []);

  if (!hasControls) {
    return (
      <div className={wrapperClasses} data-testid="student-timer-widget">
        <div
          className={clsx(
            "px4 flex justify-center items-center seconds-remaining",
            isFlashing && "timer-flashing",
          )}
        >
          <span>{timeRemainingString} remaining</span>
        </div>
      </div>
    );
  }
  return (
    <div className={wrapperClasses} data-testid="instructor-timer-widget">
      <div className="flex items-center justify-between px3">
        {classTimer.active ? (
          <IconButton className="timer-button" onClick={onPlayPause} aria-label="Pause Timer">
            <PauseIcon size="lg" width={0} height={0} />
          </IconButton>
        ) : (
          <IconButton className="timer-button" onClick={onPlayPause} aria-label="Start Timer">
            {/* TODO IconButton doesn't correctly styile this icon (and others). We should move
            away from IconButton and re-think the Icon hover/active states. See discussion
            at https://github.com/minervaproject/picasso/pull/11628#discussion_r1162632179 */}
            <PlayIcon size="lg" width={0} height={0} />
          </IconButton>
        )}
        <div className={clsx("seconds-remaining", isFlashing && "timer-flashing")}>
          {timeRemainingString}
        </div>
        <button
          type="button"
          aria-label="Decrement time by 30s"
          className="adjust-time-button text-black-tint-70"
          onClick={() => onChangeTime(-30)}
        >
          -30s
        </button>
        <button
          type="button"
          aria-label="Increment time by 30s"
          className="adjust-time-button text-black-tint-70"
          onClick={() => onChangeTime(30)}
        >
          +30s
        </button>
        <IconButton
          className="timer-button"
          onClick={onClose}
          name="Stop Timer"
          aria-label="Stop Timer"
        >
          <XCloseIcon size="lg" width={0} height={0} />
        </IconButton>
      </div>
    </div>
  );
}

function ClassTimerWidget({
  initialPosition,
  canControlClassroom,
  onClose,
  doubleSize = false,
}: {
  initialPosition: { left: number; top: number };
  canControlClassroom: boolean;
  onClose?: () => void;
  doubleSize?: boolean;
}) {
  const dispatch = useAppDispatch();
  const flightAPI = useContext(FlightAPIContext);
  const classTimer = useAppSelector(classroomSelectors.selectTimer());
  const canPlaySounds = useAppSelector(classroomSelectors.selectCurrentUserCanPlaySounds());
  const draggableRef = useRef<HTMLDivElement>(null);
  const defaultPosition = {
    x: initialPosition.left,
    y: initialPosition.top,
  };
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const handleClose = () => {
    if (canControlClassroom) {
      dispatch(endTimer());
    }
    if (onClose) {
      onClose();
    }
  };

  const handlePlayPause = () => {
    if (classTimer.active && classTimer.endsOn) {
      const timeRemainingMs =
        new Date(classTimer.endsOn).getTime() - flightAPI.getServerMilliseconds();
      dispatch(pauseTimer({ timeRemainingMs }));
    } else if (!classTimer.active) {
      const endsOn = new Date(
        flightAPI.getServerMilliseconds() +
          (classTimer.timeRemainingWhenPausedMs || classTimer.durationMs),
      ).toISOString();
      dispatch(playTimer({ endsOn }));
    } else {
      console.error({ classTimer });
      throw new Error("Unexpected timer state");
    }
  };

  const handleTimeAdjust = (timeAdjustmentInSeconds: number) => {
    let endsOn = null;
    if (classTimer.endsOn) {
      endsOn = new Date(
        new Date(classTimer.endsOn).getTime() + timeAdjustmentInSeconds * 1000,
      ).toISOString();
    }
    dispatch(adjustTimerDuration({ timeAdjustmentInSeconds, endsOn }));
  };

  return createPortal(
    <Draggable
      bounds="#root"
      nodeRef={draggableRef}
      position={position || defaultPosition}
      onDrag={(e, data) => setPosition(data)}
    >
      <div className="timer-view-wrapper" ref={draggableRef}>
        <div className={clsx(doubleSize && "double-size")}>
          <ClassTimer
            hasControls={canControlClassroom}
            canPlaySounds={canPlaySounds}
            onChangeTime={handleTimeAdjust}
            onClose={handleClose}
            onPlayPause={handlePlayPause}
          />
        </div>
      </div>
    </Draggable>,
    document.getElementById("root")!,
  );
}

function ClassTimerControls({ canControlClassroom }: { canControlClassroom: boolean }) {
  const dispatch = useAppDispatch();
  const classTimer = useAppSelector(classroomSelectors.selectTimer());
  const keyboardShortcutSignal = useAppSelector(localSelectors.selectKeyboardShortcutSignal());
  const currentPanel = useAppSelector(localSelectors.selectSidebarPanel());
  const isSidebarOpen = currentPanel !== SidebarPanelType.None;
  const [isTimerVisibleByStaff, setIsTimerVisibleByStaff] = useState(
    canControlClassroom && classTimer.open,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const iconColor =
    isTimerVisibleByStaff || classTimer.open ? "var(--blue-shade-20)" : "var(--black-tint-40)";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [initialPosition, setInitialPosition] = useState<{ left: number; top: number } | null>(
    null,
  );

  const handleToggleVisibility = useCallback(() => {
    if (!isTimerVisibleByStaff) {
      setIsTimerVisibleByStaff(true);
    }
    // To close the timer, the user must click the x button on the timer itself.
  }, [isTimerVisibleByStaff]);

  useEffect(() => {
    if (keyboardShortcutSignal === KeyboardShortcutSignalType.ToggleTimer) {
      handleToggleVisibility();
      dispatch(setKeyboardShortcutSignal({ signal: KeyboardShortcutSignalType.None }));
    }
  }, [dispatch, handleToggleVisibility, keyboardShortcutSignal]);

  useEffect(() => {
    const handleResize = throttle(() => {
      setWindowWidth(window.innerWidth);
    }, 50);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setWindowWidth]);

  useLayoutEffect(() => {
    const buttonBounds = buttonRef.current?.getBoundingClientRect();
    const timerWidth = canControlClassroom ? 204 : 148;
    const sidebarWidth = isSidebarOpen ? Settings.SIDEBAR_CONTENT_WIDTH : 0;
    setInitialPosition({
      left: windowWidth - sidebarWidth - Settings.SIDEBAR_CONTROLS_WIDTH - timerWidth - 10,
      top: buttonBounds?.top ? buttonBounds.top - 5 : window.innerHeight - 48,
    });
  }, [canControlClassroom, isSidebarOpen, windowWidth]);

  return (
    <div>
      {canControlClassroom && (
        <IconButton
          ref={buttonRef}
          className="my1"
          onClick={handleToggleVisibility}
          aria-label="Timer"
        >
          <ClockTimerIcon width={20} height={20} color={iconColor} data-testid="timer-icon" />
        </IconButton>
      )}
      {(isTimerVisibleByStaff || classTimer.open) && initialPosition && (
        <ClassTimerWidget
          onClose={() => setIsTimerVisibleByStaff(false)}
          canControlClassroom={canControlClassroom}
          // For instructors, the timer is displayed next to the sidebar timer button.
          // For students, the time is displayed at the bottom right of the stage
          initialPosition={initialPosition}
        />
      )}
    </div>
  );
}

// Note: The mobile timer bar and timer controls do _not_ have sounds
function MobileTimerControls() {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const flightAPI = useContext(FlightAPIContext);
  const breakoutSessionId = useAppSelector(classroomSelectors.selectActiveBreakoutSessionId());
  const pollSessionPane = useAppSelector(classroomSelectors.selectFirstActivePollSessionPane());
  const pollSession = useAppSelector(
    classroomSelectors.selectPollSessionById(pollSessionPane?.content_id),
  );
  const poll = useAppSelector(classroomSelectors.selectPollById(pollSession?.poll_id));
  const pollResponses = useArraySelector(
    classroomSelectors.selectPollResponsesByPollSessionId(pollSession?.id),
  );
  const pollSessionCompleted = pollSession?.status === PollSessionStatusType.Completed;
  const classTimer = useAppSelector(classroomSelectors.selectTimer());
  const isTimer = classTimer.active && !pollSessionPane && !breakoutSessionId;
  const isPoll = pollSessionPane && !breakoutSessionId;
  const isActive = breakoutSessionId || (!breakoutSessionId && classTimer.active);

  const handleTimeAdjust = (timeAdjustmentInSeconds: number) => {
    if (breakoutSessionId) {
      dispatch(adjustBreakoutSessionDuration({ id: breakoutSessionId, timeAdjustmentInSeconds }));
    } else {
      let endsOn = null;
      if (classTimer.endsOn) {
        endsOn = new Date(
          new Date(classTimer.endsOn).getTime() + timeAdjustmentInSeconds * 1000,
        ).toISOString();
      }
      dispatch(adjustTimerDuration({ timeAdjustmentInSeconds, endsOn }));
    }
  };

  const handleStartStop = () => {
    if (breakoutSessionId) {
      dispatch(endBreakoutSession());
    } else if (classTimer.active && !pollSessionPane) {
      // Pause timer
      // TODO Timer can't be removed unless in a poll and poll is stopped with it - design missing
      if (!classTimer.endsOn) {
        throw new Error("Timer is active but doesn't have an end time");
      }
      const timeRemainingMs =
        new Date(classTimer.endsOn).getTime() - flightAPI.getServerMilliseconds();
      dispatch(pauseTimer({ timeRemainingMs }));
    } else if (classTimer.active && poll && pollSession && !pollSessionCompleted) {
      // Stop poll and timer
      dispatch(endTimer());
      endPollSessionHelper(
        pollSession,
        pollResponses,
        poll.type,
        flightAPI.getServerMilliseconds(),
        dispatch,
        forumAPI,
      );
    } else if (classTimer.active && pollSession && pollSessionCompleted) {
      // Poll was stopped separately from timer, so timer is still running
      dispatch(endTimer());
    } else if (!classTimer.active && pollSessionPane) {
      // Start timer
      const endsOn = new Date(
        flightAPI.getServerMilliseconds() +
          (classTimer.timeRemainingWhenPausedMs || classTimer.durationMs),
      ).toISOString();
      dispatch(playTimer({ endsOn }));
    } else {
      throw new Error("Unexpected timer state");
    }
  };

  if (pollSessionCompleted && !classTimer.open) return null;

  // TODO Once design is updated, complete timer.test.tsx
  return (
    <div className="flex">
      <MobileCard>
        <div className="flex justify-between">
          <div>
            <Button className="mr3" kind="secondary-mobile" onClick={() => handleTimeAdjust(-30)}>
              - 0:30
            </Button>
            <Button className="" kind="secondary-mobile" onClick={() => handleTimeAdjust(30)}>
              + 0:30
            </Button>
          </div>
          {/* TODO This does not handle the case where the timer is active, but not attached to a poll */}
          <Button className="mla" kind="secondary-mobile" onClick={handleStartStop}>
            {isActive ? "End" : "Start"} {isTimer ? "Timer" : isPoll ? "Poll" : "Breakout"}
          </Button>
        </div>
      </MobileCard>
    </div>
  );
}

export { ClassTimer, ClassTimerWidget, ClassTimerControls, MobileTimerControls };
